import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../i18n";
import { request } from "../api/http";
type PlaceListItem = {
  id?: string | number;
  public_id?: string;
  name?: string;
  kind?: string;
  reviews_cnt?: number;
  avg_score?: number;
};

type PlacesResponse = { items?: PlaceListItem[] } | PlaceListItem[];

function normalizePlaces(resp: PlacesResponse): PlaceListItem[] {
  if (Array.isArray(resp)) return resp;
  return Array.isArray(resp.items) ? resp.items : [];
}

export default function PlacesPage() {
  const { t } = useI18n();

  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [places, setPlaces] = useState<PlaceListItem[]>([]);

  async function load(queryString: string) {
    setLoading(true);
    setError(null);
    try {
      const query = queryString ? `?q=${encodeURIComponent(queryString)}` : "";
      // expected endpoint: GET /api/v1/places?q=...
      const resp = await request<PlacesResponse>(`/places${query}`, {
        method: "GET",
        auth: true
      });
      setPlaces(normalizePlaces(resp));
    } catch (e: any) {
      setError(e?.message || t("places.errors.loadFailed"));
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load("");
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return places;
    return places.filter((p) => (p.name || "").toLowerCase().includes(needle));
  }, [places, q]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">{t("places.title")}</h1>
        <p className="text-sm text-slate-400">{t("places.subtitle")}</p>
      </div>

      <div className="flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("places.searchPlaceholder")}
          className="flex-1 rounded-xl bg-slate-950/70 border border-slate-700 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
        />
        <button
          onClick={() => void load(q.trim())}
          className="rounded-xl border border-slate-700 px-4 py-2 text-sm hover:border-slate-500"
        >
          {loading ? t("common.loading") : t("common.search")}
        </button>
      </div>

      {error && (
        <div className="text-xs text-rose-300 bg-rose-950/30 border border-rose-900 rounded-xl px-3 py-2">
          {error}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {(filtered ?? []).map((p) => (
          <div
            key={p.public_id || String(p.id)}
            className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4"
          >
            <div className="font-semibold">{p.name}</div>
            <div className="text-xs text-slate-400">
              {p.kind} • {p.reviews_cnt ?? 0} {t("places.reviews")} • {t("places.avg")} {p.avg_score ?? 0}
            </div>
          </div>
        ))}

        {!loading && (filtered?.length ?? 0) === 0 && (
          <div className="text-sm text-slate-400">{t("places.empty")}</div>
        )}
      </div>
    </div>
  );
}
